import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";

import WMIcon from "../../../SVGs/WMAccounts.svg";
import LI from "../../../SVGs/LI.svg";
import DI from "../../../SVGs/DI.svg";
import AI from "../../../SVGs/AI.svg";
import UI from "../../../SVGs/UI.svg";
import HO from "../../../SVGs/HO.svg";
import RI from "../../../SVGs/RI.svg";
import Dcouments from "../../../SVGs/Dcouments.svg";

function Card({
  Title,
  BackgroundColor,
  allPolicies,
  accounts,
  documents,
  type,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 719);
  const scrollRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 719);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const carouselItems = useMemo(() => {
    const items = [];
    const pushPolicy = (list, title, icon) => {
      if (list?.length > 0) {
        list.forEach(data => items.push({ category: 'policy', title, icon, data }));
      }
    };

    if (allPolicies) {
      pushPolicy(allPolicies.LI, "LI Policy", LI);
      pushPolicy(allPolicies.DI, "DI Policy", DI);
      pushPolicy(allPolicies.Auto, "Auto Policy", AI);
      pushPolicy(allPolicies.Umbrella, "Umberlla Policy", UI);
      pushPolicy(allPolicies.HomeOwners, "Home Owner Policy", HO);
      pushPolicy(allPolicies.Renters, "Renter Policy", RI);
    }
    if (accounts?.length > 0) accounts.forEach(data => items.push({ category: 'account', icon: WMIcon, data }));
    if (documents?.length > 0) documents.forEach(data => items.push({ category: 'document', icon: Dcouments, data }));
    
    return items;
  }, [allPolicies, accounts, documents]);

  const totalItems = carouselItems.length;

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || !isMobile || totalItems === 0) return;

    const handleScroll = () => {
      const itemWidth = scrollContainer.children[0].offsetWidth + 20; 
      const newIndex = Math.round(scrollContainer.scrollLeft / itemWidth);
      setActiveIndex(Math.min(Math.max(0, newIndex), totalItems - 1));
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [isMobile, totalItems]);

  return (
  <NavLink to={type === "Policies" ? "/Policies" : type === "WM" ? "/WMAccounts" : "/DocumentValut"}>
    <SubContainer BackgroundColor={BackgroundColor}>
      <TopPart>
        <SubTitle>{Title}</SubTitle>
          <DetailsBtn BackgroundColor={BackgroundColor}>Details</DetailsBtn>
      </TopPart>

      <ContentArea ref={scrollRef} isMobile={isMobile}>
        {totalItems > 0 ? (
          carouselItems.map((item, index) => (
            <RecordCard key={index} isMobile={isMobile}>
              <Icon src={item.icon} />
              <DetailsContainer>
                <Name>{item.title || item.data.hfa_number || item.data.name}</Name>
                <Info>
                  {item.category === 'account' 
                    ? item.data["hfa_balance@OData.Community.Display.V1.FormattedValue"] 
                    : (item.data.hip_name || item.data.modifiedDate)}
                </Info>
              </DetailsContainer>
            </RecordCard>
          ))
        ) : (
          <NothingToShow>
            {type === "Policies" && "No active policies found."}
            {type === "WM" && "No wealth management accounts found."}
            {type === "documents" && "No files need review."}
          </NothingToShow>
        )}
      </ContentArea>

      {isMobile && totalItems > 1 && (
        <DotsContainer>
          {carouselItems.map((_, index) => (
            <Dot key={index} isActive={index === activeIndex} activeColor={BackgroundColor} />
          ))}
        </DotsContainer>
      )}
    </SubContainer>
            </NavLink>

  );
}


const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  box-shadow: 0px 0px 3px 0.1px gray;
  background: white;
  overflow: hidden;
  position: relative;
  margin-bottom: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 15px;
    background: ${props => props.BackgroundColor};
  }
`;

const TopPart = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px 20px 10px 20px;
`;

const SubTitle = styled.div`
  color: #304961;
  font-family: "Museo Sans Cyrl";
  font-size: 28px;
  font-weight: 400;
    line-height: 1;
`;

const DetailsBtn = styled.div`
  color: #304961;
  font-family: "Museo Sans Cyrl";
  font-size: 14px;
  font-weight: 700;
  border: 1px solid ${props => props.BackgroundColor};
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: 0.2s;
  &:hover { background: ${props => props.BackgroundColor}; color: white; }
`;

const ContentArea = styled.div`
  display: flex;
  gap: 20px;
  padding: 10px 20px 20px 20px;

  /* Switch Layout Logic */
  ${props => props.isMobile ? `
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  ` : `
    flex-wrap: wrap;
    overflow-x: visible;
  `}
`;

const RecordCard = styled.div`
  display: flex;
  align-items: center;
  background: white;
  padding: 20px;
  border-radius: 20px;
  box-shadow: 0px 2px 8px rgba(0,0,0,0.05);
  border: 1px solid #f0f0f0;

  /* Mobile: Single card snap. Desktop: Grid-like wrap */
  ${props => props.isMobile ? `
    scroll-snap-align: center;
    flex: 0 0 calc(100% - 10px);
    min-width: 280px;
  ` : `
    flex: 0 1 280px;
  `}
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding-bottom: 20px;
`;

const Dot = styled.div`
  height: 8px;
  width: ${props => props.isActive ? '18px' : '8px'};
  background-color: ${props => props.isActive ? props.activeColor : '#e0e0e0'};
  border-radius: 10px;
  transition: 0.3s ease;
`;

const Icon = styled.img` width: 50px; height: 50px; margin-right: 15px; `;
const DetailsContainer = styled.div` display: flex; flex-direction: column; `;
const Name = styled.div` color: #304961; font-weight: 600; font-size: 17px; `;
const Info = styled.div` color: #304961; font-style: italic; font-size: 13px; margin-top: 4px; `;
const NothingToShow = styled.div` width: 100%; text-align: center; color: gray; padding: 40px; font-size: 18px; `;

export default Card;